import { type FC, useEffect, useState } from 'react';
import { Form, Image } from 'react-bootstrap';

import { type Chapter } from '@editor2/types/chapter';
import { updateChapter } from '@editor2/reducers/editor/editorSlice';
import { chapterEditorContainer } from '@editor2/containers/ChapterPage/styles';
import { chapterToChapterProperties } from '@editor2/mappers/chapter';
import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import CopyInput from '@editor2/components/CopyInputWrapper';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';

interface Props {
	chapter: Chapter;
	courseId: number;
}

const ChapterEditorForm: FC<Props> = (props) => {
	const { chapter, courseId } = props;
	const initProperties = chapterToChapterProperties(chapter);

	const commonPayload = { chapterId: chapter.family_id, courseId };

	const debouncedDispatch = useDebouncedDispatch();

	const [chapterProperties, setChapterProperties] = useState({ ...initProperties });

	useEffect(() => {
		const updatedChapterProperties = chapterToChapterProperties(chapter);
		setChapterProperties({ ...updatedChapterProperties });
	}, [chapter]);

	const handleChange = (value) => {
		setChapterProperties({ ...chapterProperties, ...value });
		debouncedDispatch(updateChapter({ ...commonPayload, ...value }));
	};

	const handleImageCaptionChange = (value: string) => handleChange({ image_caption: value });

	const isUnnumbered = chapter.chapter_number === ' ';

	return (
		<Form className={chapterEditorContainer}>
			<Form.Group>
				{isUnnumbered && (
					<Form.Check
						type="checkbox"
						checked={true}
						className="chapterCheckbox"
						onChange={() => handleChange({ chapter_number: '', instructor_only: false })}
						label="The chapter does not take part in renumbering"
					/>
				)}
				{!isUnnumbered && (
					<Form.Control
						size="sm"
						className="chapterNumber"
						placeholder="Chapter number"
						value={chapterProperties.chapter_number}
						onChange={(e) => handleChange({ chapter_number: e.target.value })}
					/>
				)}

				{isUnnumbered && (
					<Form.Check
						type="checkbox"
						checked={!!chapterProperties.instructor_only}
						className="chapterCheckbox"
						onChange={() => handleChange({ instructor_only: !chapterProperties.instructor_only })}
						label="Instructor-only chapter"
					/>
				)}
			</Form.Group>
			<Form.Group>
				<Form.Control
					size="lg"
					className="chapterName"
					value={chapterProperties.name}
					onChange={(e) => handleChange({ name: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Image URL</Form.Label>
				<Form.Control
					value={chapterProperties.image_url}
					onChange={(e) => handleChange({ image_url: e.target.value })}
				/>
			</Form.Group>
			<div className="chapterImageContainer">
				{chapterProperties.image_url && (
					<Image
						src={chapterProperties.image_url}
						className="chapterImage"
						alt="Chapter image"
						thumbnail
					/>
				)}
			</div>

			<Form.Group>
				<Form.Label>Image Thumbnail URL</Form.Label>
				<Form.Control
					value={chapterProperties.image_thumbnail_url}
					onChange={(e) => handleChange({ image_thumbnail_url: e.target.value })}
				/>
			</Form.Group>
			<div className="chapterImageContainer">
				{chapterProperties.image_thumbnail_url && (
					<Image
						src={chapterProperties.image_thumbnail_url}
						className="chapterImage"
						alt="Chapter banner"
						thumbnail
					/>
				)}
			</div>

			<Form.Group>
				<Form.Label>Image caption</Form.Label>
				<CodeMirrorHTML
					value={chapterProperties.image_caption}
					onChange={handleImageCaptionChange}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Image Accessibility Description</Form.Label>
				<Form.Control
					as="textarea"
					rows={3}
					value={chapterProperties.image_accessibility_description}
					onChange={(e) => handleChange({ image_accessibility_description: e.target.value })}
				/>
			</Form.Group>

			<CopyInput label="Family ID" value={chapterProperties.family_id} />

			<Form.Group>
				<Form.Label>End-of-Chapter Message</Form.Label>
				<Form.Control
					as="textarea"
					rows={3}
					value={chapterProperties.end_of_chapter_message}
					onChange={(e) => handleChange({ end_of_chapter_message: e.target.value })}
				/>
			</Form.Group>
		</Form>
	);
};

export default ChapterEditorForm;
